import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  createZone,
  getZone,
  listCompaniesLite,
  listUsersLite,
  updateZone,
  listZoneStates,
  setZoneStates,
  type CompanyLite,
  type UserLite,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';
import { MembershipPicker } from './MembershipPicker.js';

export function ZoneFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { zoneId } = useParams<{ zoneId: string }>();
  const editing = Boolean(zoneId);

  const [companyId, setCompanyId] = useState('');
  const [zoneCode, setZoneCode] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [zoneHeadId, setZoneHeadId] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [companies, setCompanies] = useState<CompanyLite[]>([]);
  const [users, setUsers] = useState<UserLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listCompaniesLite(token).then((r) => setCompanies(r.data)).catch((e) => setError((e as Error).message));
    listUsersLite(token).then((r) => setUsers(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!zoneId) return;
    getZone(token, zoneId)
      .then((z) => {
        setCompanyId(z.companyId ?? '');
        setZoneCode(z.zoneCode);
        setZoneName(z.zoneName);
        setZoneHeadId(z.zoneHeadId ?? '');
        setEffectiveFromDate(z.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, zoneId]);

  async function submit(): Promise<void> {
    if (!zoneName.trim()) { setError(t('org.zone.form.nameReq')); return; }
    if (!editing) {
      if (!zoneCode.trim()) { setError(t('org.zone.form.codeReq')); return; }
      if (!companyId) { setError(t('org.zone.form.companyReq')); return; }
    }
    setError(null);
    setBusy(true);
    try {
      if (editing && zoneId) {
        await updateZone(token, zoneId, {
          zoneName: zoneName.trim(),
          zoneHeadId: zoneHeadId || undefined,
        });
      } else {
        await createZone(token, {
          companyId,
          zoneCode: zoneCode.trim(),
          zoneName: zoneName.trim(),
          zoneHeadId: zoneHeadId || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/zones');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const companyOptions = companies.map((c) => ({ value: c.id, label: `${c.companyCode} — ${c.companyName}` }));
  const userOptions = users.map((u) => ({ value: u.userId, label: u.displayName }));

  return (
    <FormShell label="zone-form" title={editing ? t('org.zone.form.editTitle') : t('org.zone.form.newTitle')} error={error} onSubmit={submit}>
      {!editing && (
        <SelectField label={t('org.field.company')} name="companyId" value={companyId} onChange={setCompanyId} options={companyOptions} required />
      )}
      <Field label={t('org.field.code')} name="zoneCode" value={zoneCode} onChange={setZoneCode} required placeholder={t('org.zone.form.codePh')} />
      <Field label={t('org.field.name')} name="zoneName" value={zoneName} onChange={setZoneName} required />
      <SelectField label={t('org.field.zoneHead')} name="zoneHeadId" value={zoneHeadId} onChange={setZoneHeadId} options={userOptions} placeholder={t('org.common.noneNone')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.zone.form.create')} cancelTo="/org/zones" />
      {editing && zoneId ? (
        <MembershipPicker
          testId="zone-states"
          title={t('org.zone.form.statesTitle')}
          emptyHint={t('org.zone.form.statesEmpty')}
          load={async () =>
            (await listZoneStates(token, zoneId)).map((s) => ({
              id: s.stateId, code: s.stateCode, name: s.stateName,
              assigned: s.assigned, takenBy: s.assignedToOtherZoneId,
            }))
          }
          save={async (ids) =>
            (await setZoneStates(token, zoneId, ids)).map((s) => ({
              id: s.stateId, code: s.stateCode, name: s.stateName,
              assigned: s.assigned, takenBy: s.assignedToOtherZoneId,
            }))
          }
        />
      ) : null}
    </FormShell>
  );
}
